'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  FileText,
  ListMusic,
  Music,
  Music2,
  Play,
  SkipBack,
  SkipForward,
  TrendingUp,
  Volume2,
  VolumeOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  isMusicMuted,
  isSoundMuted,
  toggleMusicMuted,
  toggleSoundMuted,
} from '@/lib/sounds';
import { getMusicPlayer, MUSIC_PLAYLIST } from '../audio/music-player';
import { TAKEOVER_FLAUNCH_COIN_URL } from '@/lib/constants/protocol-branding';
import { XIcon } from '../ui/BrandIcons';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { TermsDialog } from '@/components/legal/terms-dialog';
import { useWallet } from '@/hooks/use-wallet';
import { NotificationsDropdown } from '@/features/lobby/nav/NotificationsDropdown';

interface LobbyFooterProps {
  className?: string;
}

export function LobbyFooter({ className }: LobbyFooterProps) {
  const [termsOpen, setTermsOpen] = useState(false);
  // The Alerts pill (next to SFX) is only meaningful for connected
  // viewers — guests see no live notifications and the pill would
  // open a guest-mode dropdown that doesn't apply to anonymous
  // sessions. Hidden until a wallet is linked, mirroring how
  // `AppBottomNav` swaps Profile for Connect when disconnected.
  const { address: walletAddress } = useWallet();
  const {
    marketCapUsd,
    priceUsd,
    isStale,
    isUnavailable,
    marketCapChangePercent24h,
  } = useTakeoverPrice();
  const [sfxMuted, setSfxMuted] = useState(false);
  // Music now defaults to muted (see `isMusicMuted` in sounds.ts).
  // Match that on first paint so the pill icon doesn't flash "unmuted"
  // during the brief window between SSR/initial render and the
  // localStorage-reading effect below.
  const [musicMuted, setMusicMuted] = useState(true);
  const [trackIndex, setTrackIndex] = useState(0);
  const [musicReady, setMusicReady] = useState(false);
  // Mirror of the music-player's enabled-tracks set so the footer can
  // render checkboxes without poking at the player on every render. We
  // seed with all indices to match the player's default, then
  // `playlist-changed` keeps us honest once the player restores from
  // localStorage or the user toggles a row.
  const [enabledIndices, setEnabledIndices] = useState<number[]>(() =>
    MUSIC_PLAYLIST.map((_, i) => i),
  );

  // Headline label sits next to the MC pill: shows live status when
  // we have a fresh quote, "stale" past the 60s threshold, and
  // "unavailable" when neither the V3 nor V4 oracle could be read.
  const status: 'live' | 'stale' | 'unavailable' = isUnavailable
    ? 'unavailable'
    : isStale
      ? 'stale'
      : 'live';

  // Keep the footer toggles in sync with the global mute state — other UI
  // (mobile controller, tutorial, etc.) can flip these and we listen on the
  // same custom events that `sounds.ts` dispatches.
  useEffect(() => {
    setSfxMuted(isSoundMuted());
    setMusicMuted(isMusicMuted());
    const onSfx = () => setSfxMuted(isSoundMuted());
    const onMusic = () => setMusicMuted(isMusicMuted());
    window.addEventListener('sound-mute-changed', onSfx);
    window.addEventListener('music-mute-changed', onMusic);
    return () => {
      window.removeEventListener('sound-mute-changed', onSfx);
      window.removeEventListener('music-mute-changed', onMusic);
    };
  }, []);

  // Subscribe to the music-player singleton so the footer can render the
  // current track name and keep in sync with autoplay/crossfades/next/prev.
  // The player emits `track-changed` on successful crossfade,
  // `state-changed` when it becomes ready or the playing flag flips, and
  // `playlist-changed` whenever the user toggles a track on/off or the
  // persisted selection is restored on boot.
  useEffect(() => {
    const player = getMusicPlayer();
    const sync = () => {
      const s = player.getState();
      setTrackIndex(s.currentIndex);
      setMusicReady(s.ready);
    };
    const syncPlaylist = () => {
      setEnabledIndices(player.getEnabledIndices());
    };
    sync();
    syncPlaylist();
    const offTrack = player.subscribe('track-changed', sync);
    const offState = player.subscribe('state-changed', sync);
    const offPlaylist = player.subscribe('playlist-changed', syncPlaylist);
    return () => {
      offTrack();
      offState();
      offPlaylist();
    };
  }, []);

  const currentTrack = MUSIC_PLAYLIST[trackIndex] ?? MUSIC_PLAYLIST[0];

  return (
    <div
      className={cn(
        // Trimmed from `gap-3 px-4` to `gap-2 px-3` + horizontal
        // overflow-x-auto so SFX / Music controls never spill off the
        // viewport on narrow phones. The scroller is one-axis only so
        // vertical spill from popovers (e.g. playlist) still works.
        'relative z-10 flex items-center gap-2 px-3 h-9 bg-black/85 border-t border-amber-500/25 text-[9px] font-mono tracking-[0.2em] uppercase text-zinc-400 shrink-0',
        'overflow-x-auto overflow-y-visible [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setTermsOpen(true)}
        className="flex items-center gap-1 text-zinc-400 hover:text-amber-100 transition-colors shrink-0"
      >
        <FileText className="h-2.5 w-2.5" />
        Terms
      </button>

      <span className="text-zinc-700 shrink-0">·</span>

      {/* Plain X icon — the "@takeoverfun" label was repeating the
          destination and eating room the SFX / Music / MC chips
          needed on narrow phones. */}
      <a
        href="https://x.com/takeoverfun"
        target="_blank"
        rel="noreferrer"
        aria-label="Takeover on X"
        title="Takeover on X"
        className="flex items-center text-zinc-400 hover:text-amber-100 transition-colors shrink-0"
      >
        <XIcon size={10} className="text-current" />
      </a>

      <div className="flex-1 min-w-[8px]" />

      {/* Alerts pill — primary mobile entry into the notifications
          dropdown. Sits immediately to the LEFT of SFX so the
          notification surface is reachable without leaving the
          bottom-edge thumb zone, and uses Radix's `side="top"` so
          the dropdown panel "drops up" away from the bottom chrome
          rather than colliding with the AppBottomNav row below.
          Only rendered for connected viewers (guests would see an
          empty dropdown). Hidden on `lg+` because the desktop
          LobbyNav already carries the framed bell — duplicating
          it in the footer would look like a second, redundant
          alerts surface on wide screens. `align="start"` anchors
          the panel to the pill's left edge so it fans rightward
          into the available footer width on narrow phones. */}
      {walletAddress && (
        <div className="lg:hidden flex items-center shrink-0">
          <NotificationsDropdown variant="footer-pill" side="top" align="start" />
        </div>
      )}

      {/* `data-tutorial-audio-controls` is the input-guard whitelist
          marker — `tutorial-input-guard.ts` checks `closest(...)` for
          this attribute so the SFX / Music toggles stay clickable
          while the tutorial is running. Without it the tutorial's
          window-level pointer guard would swallow the click and the
          user would be locked into whatever audio state they had
          on entry. */}
      <div className="flex items-center gap-1 shrink-0" data-tutorial-audio-controls>
        <AudioToggle
          label="SFX"
          muted={sfxMuted}
          onToggle={() => setSfxMuted(toggleSoundMuted())}
          iconOn={<Volume2 className="h-2.5 w-2.5" />}
          iconOff={<VolumeOff className="h-2.5 w-2.5" />}
        />
        {/* Music is desktop-only on the footer — on phones we hide the
            toggle AND the music player never starts (see
            `MusicController`, which gates gesture wiring on `!isMobile`).
            Keeping SFX visible so the user can still dismiss
            confirmation chimes on mobile. */}
        <div className="hidden md:flex items-center">
          <MusicToggle
            muted={musicMuted}
            ready={musicReady}
            trackTitle={currentTrack.title}
            trackIndex={trackIndex}
            enabledIndices={enabledIndices}
            onToggle={() => setMusicMuted(toggleMusicMuted())}
            onPrev={() => getMusicPlayer().previous()}
            onNext={() => getMusicPlayer().next()}
            onSetTrackEnabled={(index, enabled) =>
              getMusicPlayer().setTrackEnabled(index, enabled)
            }
          />
        </div>
      </div>

      {/* MC pill now renders on every breakpoint. On <md we collapse
          "$TAKEOVER MC" → "$TAKEOVER" and hide the 24h badge so the
          pill still fits next to SFX/Music on a ~360px viewport. The
          full copy comes back at md+ where there's room. */}
      <span className="flex items-center gap-2 shrink-0">
        <a
          href={TAKEOVER_FLAUNCH_COIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center gap-1.5 px-2 h-5 rounded-[3px] border tabular-nums cursor-pointer transition-colors hover:border-amber-400/50 hover:bg-amber-500/15',
            status === 'unavailable'
              ? 'bg-zinc-500/10 border-zinc-500/30 text-zinc-400'
              : status === 'stale'
                ? 'bg-amber-500/5 border-amber-500/20 text-amber-200/70'
                : 'bg-amber-500/10 border-amber-500/30 text-amber-100',
          )}
          title={
            status === 'unavailable'
              ? '$TAKEOVER price oracle unavailable · opens Flaunch'
              : status === 'stale'
                ? 'Last live quote is older than 60s · opens Flaunch'
                : `$${formatPriceUsd(priceUsd)} per TAKEOVER · view on Flaunch`
          }
        >
          <TrendingUp className="h-2.5 w-2.5 shrink-0" />
          <span>
            $TAKEOVER<span className="hidden md:inline"> MC</span>
          </span>
          <span>
            {status === 'unavailable'
              ? '—'
              : `$${formatCompact(marketCapUsd)}`}
          </span>
          {status !== 'unavailable' && (
            <span className="hidden md:flex">
              <Change24hBadge percent={marketCapChangePercent24h} />
            </span>
          )}
        </a>
      </span>

      <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} />
    </div>
  );
}

function formatCompact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toFixed(0);
}

/**
 * 24h delta pill rendered next to the live $TAKEOVER MC. Renders `—`
 * (with a tooltip) until the snapshot table has at least one entry
 * older than ~20h, so we don't lie with `+0%` while the rolling
 * history is warming up. Sub-percent moves render as `~0%` because
 * the magnitude is meaningful (not yet broken) but rounding to a
 * single decimal would otherwise read as exactly zero.
 */
function Change24hBadge({ percent }: { percent: number | null }) {
  if (percent === null || !Number.isFinite(percent)) {
    return (
      <span
        className="text-zinc-500 text-[8px] tracking-[0.18em] uppercase"
        title="24h change unavailable — rolling history is still warming up"
      >
        24h —
      </span>
    );
  }
  const isPositive = percent >= 0;
  const abs = Math.abs(percent);
  const formatted = abs >= 100
    ? `${abs.toFixed(0)}%`
    : abs >= 10
      ? `${abs.toFixed(1)}%`
      : abs >= 1
        ? `${abs.toFixed(2)}%`
        : abs >= 0.05
          ? `${abs.toFixed(2)}%`
          : '~0%';
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-0.5 text-[8.5px] tracking-[0.12em]',
        isPositive ? 'text-emerald-300' : 'text-rose-300',
      )}
      title="24h change in TAKEOVER market cap"
    >
      <span aria-hidden>{isPositive ? '▲' : '▼'}</span>
      <span className="tabular-nums">{formatted}</span>
    </span>
  );
}

/** $TAKEOVER often trades at sub-dollar prices, so a fixed 2dp display
 * collapses to "$0.00" and reads as "no data". Render with adaptive
 * precision: cents and up at 4dp, sub-cent at 6 sig-figs in scientific
 * form so the magnitude is still legible in the tooltip. */
function formatPriceUsd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0.00';
  if (n >= 0.01) return n.toFixed(4);
  return n.toExponential(2);
}

/**
 * Compact audio toggle for the fixed footer. Reads as a pill: an icon,
 * the channel label, and an on/off affordance. The muted state uses a
 * dimmer icon and a slashed outline so both states are legible at this
 * tiny size without a separate tooltip.
 */
function AudioToggle({
  label,
  muted,
  onToggle,
  iconOn,
  iconOff,
}: {
  label: string;
  muted: boolean;
  onToggle: () => void;
  iconOn: React.ReactNode;
  iconOff: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={!muted}
      title={`${label} · ${muted ? 'muted' : 'on'} — click to toggle`}
      className={cn(
        'flex items-center gap-1.5 px-1.5 h-5 rounded-[3px] border transition-colors',
        muted
          ? 'border-white/10 bg-transparent text-zinc-500 hover:text-amber-100 hover:border-amber-400/30'
          : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200 hover:border-emerald-300/50',
      )}
    >
      {muted ? iconOff : iconOn}
      <span className="text-[9px] tracking-[0.2em]">{label}</span>
    </button>
  );
}

/**
 * Music pill: mute toggle + clickable track title + prev / next skip buttons.
 *
 * Wider than the SFX toggle because it carries the track name so listeners
 * can tell what's playing without pausing to inspect the audio graph.
 *
 * The title lives inside a fixed-width "ticker" that scrolls
 * right-to-left on loop — short enough to keep the footer quiet, wide
 * enough to read a couple of characters at a glance. The marquee uses
 * a duplicated text + `translateX(0 → -50%)` transform so the loop is
 * seamless. Remounted by `key` on every track change so the new title
 * always starts from the right edge, and `motion-reduce:animate-none`
 * honours the OS preference.
 *
 * Clicking the ticker opens a drop-up with the full playlist and a
 * checkbox per track so listeners can curate what the player cycles
 * through. The drop-up is rendered into a portal + fixed positioning so
 * it can float above the footer without fighting any ancestor
 * `overflow:hidden` on the lobby shell.
 *
 * The prev/next buttons are gated on the mute state: when music is
 * muted, skipping tracks would silently restart playback on a new song
 * at target volume, which users experience as the player turning itself
 * back on. We disable both buttons (and the player guards the same case
 * defensively) so track navigation stays a deliberate "un-mute first"
 * action.
 */
function MusicToggle({
  muted,
  ready,
  trackTitle,
  trackIndex,
  enabledIndices,
  onToggle,
  onPrev,
  onNext,
  onSetTrackEnabled,
}: {
  muted: boolean;
  ready: boolean;
  trackTitle: string;
  trackIndex: number;
  enabledIndices: number[];
  onToggle: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSetTrackEnabled: (index: number, enabled: boolean) => void;
}) {
  // Before the first user gesture the music player hasn't bootstrapped,
  // so we show "— waiting —" instead of a misleading track name.
  const displayTitle = ready ? trackTitle : 'click anywhere to start';

  const tickerRef = useRef<HTMLButtonElement | null>(null);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const enabledSet = useMemo(() => new Set(enabledIndices), [enabledIndices]);

  return (
    <div
      className={cn(
        'flex items-center h-5 rounded-[3px] border transition-colors',
        muted
          ? 'border-white/10 bg-transparent text-zinc-500'
          : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={!muted}
        title={`Music · ${muted ? 'muted' : 'on'} — click to toggle`}
        className={cn(
          'flex items-center gap-1 pl-1.5 pr-1 h-full transition-colors',
          muted
            ? 'hover:text-amber-100'
            : 'hover:text-emerald-100',
        )}
      >
        {muted ? (
          <Music2 className="h-2.5 w-2.5" />
        ) : (
          <Music className="h-2.5 w-2.5" />
        )}
        <span className="text-[9px] tracking-[0.2em]">MUSIC</span>
      </button>

      <span className="h-3 w-px bg-current opacity-20 mx-1" />

      <TrackTicker
        ref={tickerRef}
        title={displayTitle}
        expanded={playlistOpen}
        onClick={() => setPlaylistOpen((v) => !v)}
      />

      {/* Track navigation is gated on both "player is ready" AND "music
          is not muted". Skipping while muted would silently advance the
          cursor and then un-mute the new track at full target volume —
          users consistently report this as the player "turning back on
          by itself". Disabling the buttons while muted keeps navigation
          honest: if you want a different track, un-mute first. */}
      <button
        type="button"
        onClick={onPrev}
        disabled={!ready || muted}
        aria-label="Previous track"
        title={muted ? 'Un-mute music to skip tracks' : 'Previous track'}
        className={cn(
          'ml-1 h-full px-1 flex items-center transition-colors',
          ready && !muted
            ? 'hover:text-amber-100 text-current'
            : 'text-zinc-600 cursor-not-allowed',
        )}
      >
        <SkipBack className="h-2.5 w-2.5" />
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!ready || muted}
        aria-label="Next track"
        title={muted ? 'Un-mute music to skip tracks' : 'Next track'}
        className={cn(
          'pr-1.5 h-full px-1 flex items-center transition-colors',
          ready && !muted
            ? 'hover:text-amber-100 text-current'
            : 'text-zinc-600 cursor-not-allowed',
        )}
      >
        <SkipForward className="h-2.5 w-2.5" />
      </button>

      {playlistOpen ? (
        <PlaylistPopover
          anchorRef={tickerRef}
          currentIndex={trackIndex}
          enabledSet={enabledSet}
          onSetTrackEnabled={onSetTrackEnabled}
          onPlayTrack={(index) => getMusicPlayer().playTrackAt(index)}
          onClose={() => setPlaylistOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Short fixed-width ticker for the current track title. Renders as a
 * button so clicking the name opens the playlist drop-up — matches the
 * mental model of tapping a track in most music apps to get "more
 * options" for what should be playing.
 *
 * The title scrolls right → left on an infinite loop, resetting cleanly
 * whenever the title changes (via `key={title}`). The inner row holds
 * two copies of the text separated by a gap so the animation can loop
 * seamlessly: translating from `0` to `-50%` lines up the second copy's
 * start with where the first copy began.
 *
 * Width is tuned to fit 6–8 characters of the tracking-wide footer
 * font — intentionally narrow so the rest of the footer stays quiet.
 * `motion-reduce:animate-none` freezes the ticker for users who've
 * asked the OS to limit animation, which still reads (the opening
 * glyphs are visible) and avoids a subtle peripheral distraction.
 */
interface TrackTickerProps {
  title: string;
  expanded: boolean;
  onClick: () => void;
}

const TrackTicker = forwardRef<HTMLButtonElement, TrackTickerProps>(
  function TrackTicker({ title, expanded, onClick }, ref) {
    // Pace the scroll off title length so short names don't whip past
    // and long ones don't drag — rounds to a minimum of 8s and adds
    // ~0.25s per character beyond the visible window.
    const durationSeconds = Math.max(8, Math.round(title.length * 0.35 + 4));

    // Soft fade at the right edge so the outgoing glyph doesn't hard-clip
    // against the adjacent separator; reads as a vintage LCD ticker.
    // Implemented via CSS mask so it fades the text itself regardless of
    // the pill's background colour (which changes with mute state).
    const EDGE_FADE =
      'linear-gradient(to right, black 0%, black 82%, transparent 100%)';

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-label={`Now playing: ${title}. Click to open playlist.`}
        title={`${title} — click to edit playlist`}
        // Keying on the title forces a remount on every track change —
        // the marquee then starts fresh from its first frame (full title
        // visible on the left) rather than mid-scroll. Also note: even
        // pre-gesture (player not yet booted) the ticker stays clickable
        // so users can curate their playlist before the first
        // interaction — the MusicController's global `pointerdown`
        // listener still fires from this click and boots the player in
        // the background.
        key={title}
        className={cn(
          'relative overflow-hidden w-[72px] sm:w-[96px] h-full flex items-center transition-colors text-left cursor-pointer hover:text-amber-100',
          expanded ? 'text-amber-100' : '',
        )}
        style={{
          maskImage: EDGE_FADE,
          WebkitMaskImage: EDGE_FADE,
        }}
      >
        {/* Keyframes defined inline so the ticker is self-contained; the
            style tag is cheap and React will de-dupe it in the DOM when
            multiple footers mount (they currently don't). */}
        <style>{`
          @keyframes footer-track-ticker {
            from { transform: translate3d(0, 0, 0); }
            to   { transform: translate3d(-50%, 0, 0); }
          }
        `}</style>
        <div
          className="flex whitespace-nowrap will-change-transform motion-reduce:!animate-none"
          style={{
            animation: `footer-track-ticker ${durationSeconds}s linear infinite`,
          }}
        >
          {/* Two copies + a trailing spacer so the loop seam is a
              consistent gap rather than the last character of one
              copy abutting the first character of the next. */}
          <span className="text-[9px] tracking-[0.15em] normal-case pr-6">
            {title}
          </span>
          <span
            aria-hidden
            className="text-[9px] tracking-[0.15em] normal-case pr-6"
          >
            {title}
          </span>
        </div>
      </button>
    );
  },
);

/**
 * Playlist drop-up anchored above the track ticker. Rendered into
 * `document.body` via a portal so the footer's narrow height / the
 * lobby shell's occasional `overflow-hidden` can't clip it. Position
 * is recomputed on window resize / scroll so it tracks the anchor
 * through layout changes (e.g. the market-cap pill wrapping on small
 * viewports).
 *
 * Interaction rules:
 * - Click outside the popover OR Escape closes it.
 * - Checking / un-checking a track is handled by the caller so the
 *   underlying player state (and localStorage) stays the source of
 *   truth; this component just emits `onSetTrackEnabled` events.
 * - The currently-playing track always shows a small "now playing"
 *   marker even when disabled, so it's clear which track the next
 *   skip / auto-advance will drop.
 * - The player refuses to leave the playlist empty — when the user has
 *   narrowed the rotation down to a single track, that row renders as
 *   "locked on" (non-interactive) rather than allowing a click that
 *   would silently no-op.
 */
function PlaylistPopover({
  anchorRef,
  currentIndex,
  enabledSet,
  onSetTrackEnabled,
  onPlayTrack,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  currentIndex: number;
  enabledSet: Set<number>;
  onSetTrackEnabled: (index: number, enabled: boolean) => void;
  onPlayTrack: (index: number) => void;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    right: number;
    bottom: number;
  } | null>(null);

  // Compute the drop-up position from the anchor button's viewport
  // rect. We right-align to the anchor so the popover grows leftward
  // (there's plenty of space to the left of the music pill, but the
  // footer hugs the right edge of the viewport so growing rightward
  // would overflow on narrow screens). `useLayoutEffect` runs before
  // paint so the popover never visibly jumps into place on open.
  const recompute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    setPosition({
      right: Math.max(8, viewportW - rect.right),
      bottom: Math.max(8, viewportH - rect.top + 6),
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    // Coalesce resize + scroll bursts into one layout read per
    // animation frame. The popover's anchor only changes position
    // when the document layout shifts, so reading
    // `getBoundingClientRect` on every individual scroll/resize
    // event is purely wasted work — and on the lobby's
    // `<scroll capture>` listener it accumulated as a measurable
    // "forced reflow" hotspot in the performance trace, especially
    // while the chat virtual-scroller fires fast scroll events.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        recompute();
      });
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [recompute]);

  // Close on outside click / Escape. We listen on `mousedown` rather
  // than `click` so closing beats any competing click handler (e.g. the
  // board canvas's gesture boot that absorbs the click to start the
  // music player on first interaction).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const node = e.target as Node;
      if (popoverRef.current?.contains(node)) return;
      if (anchorRef.current?.contains(node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorRef, onClose]);

  if (typeof document === 'undefined') return null;
  // Before the first layout we don't know where to draw the popover;
  // suppress the initial paint so it doesn't flash at (0,0) before
  // snapping into place.
  if (!position) return null;

  const enabledCount = enabledSet.size;
  const total = MUSIC_PLAYLIST.length;
  const onlyOneEnabled = enabledCount <= 1;

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Playlist"
      // Tag the portaled popover with the same audio-controls
      // marker as the footer wrapper so the tutorial input guard
      // lets clicks land here too — without this the user could
      // open the playlist (the trigger is inside the wrapper) but
      // not interact with any of its rows.
      data-tutorial-audio-controls
      className="fixed z-50 w-[260px] rounded-[4px] border border-amber-500/30 bg-[#0b0a08]/95 shadow-[0_8px_24px_rgba(0,0,0,0.55)] backdrop-blur-sm text-zinc-100 font-mono"
      style={{ right: position.right, bottom: position.bottom }}
    >
      <div className="flex items-center justify-between gap-2 px-3 h-7 border-b border-amber-500/20">
        <span className="flex items-center gap-1.5 text-[9px] tracking-[0.25em] uppercase text-amber-100">
          <ListMusic className="h-3 w-3" />
          Playlist
        </span>
        <span className="text-[9px] tracking-[0.2em] uppercase tabular-nums text-zinc-500">
          {enabledCount}/{total} on
        </span>
      </div>

      <ul role="listbox" aria-multiselectable className="py-1 max-h-[280px] overflow-y-auto">
        {MUSIC_PLAYLIST.map((track, index) => {
          const enabled = enabledSet.has(index);
          const isCurrent = index === currentIndex;
          // Refuse to unhook the last enabled track from the UI side
          // too — matches the player guard so the button shows its
          // "locked on" state rather than silently failing.
          const lockedOn = enabled && onlyOneEnabled;
          return (
            <li key={track.src}>
              <div
                className={cn(
                  'group w-full flex items-center gap-2 px-3 h-7 transition-colors',
                  lockedOn ? '' : 'hover:bg-amber-500/10',
                )}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={enabled}
                  aria-disabled={lockedOn}
                  onClick={() => {
                    if (lockedOn) return;
                    onSetTrackEnabled(index, !enabled);
                  }}
                  className={cn(
                    'flex flex-1 min-w-0 items-center gap-2 text-left h-full -ml-3 pl-3',
                    lockedOn
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer',
                  )}
                  title={
                    lockedOn
                      ? 'At least one track must stay enabled'
                      : enabled
                        ? 'Remove from rotation'
                        : 'Add to rotation'
                  }
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid place-content-center h-3.5 w-3.5 rounded-[2px] border shrink-0 transition-colors',
                      enabled
                        ? 'bg-amber-400/80 border-amber-300 text-black'
                        : 'bg-transparent border-zinc-600 text-transparent group-hover:border-amber-400/60',
                    )}
                  >
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  <span className="text-[9px] tracking-[0.18em] uppercase tabular-nums text-zinc-500 shrink-0">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span
                    className={cn(
                      'flex-1 text-[11px] tracking-[0.05em] truncate',
                      enabled ? 'text-zinc-100' : 'text-zinc-500',
                    )}
                  >
                    {track.title}
                  </span>
                </button>
                {isCurrent ? (
                  <span
                    aria-hidden
                    className="text-[8px] tracking-[0.25em] uppercase text-emerald-300 shrink-0"
                  >
                    ●
                  </span>
                ) : null}
                <button
                  type="button"
                  aria-label={isCurrent ? `Now playing: ${track.title}` : `Play ${track.title}`}
                  title={isCurrent ? 'Now playing' : 'Play this track'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlayTrack(index);
                  }}
                  className={cn(
                    'grid place-content-center h-6 w-6 shrink-0 rounded-[2px] border transition-colors',
                    isCurrent
                      ? 'border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/15'
                      : 'border-zinc-600 text-zinc-400 hover:border-amber-400/50 hover:text-amber-100',
                  )}
                >
                  <Play className="h-3 w-3" fill="currentColor" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body,
  );
}
